import { InviteWorkspaceUser } from "./InviteWorkspaceUser";
import { useEffect, useState, useRef } from "react";
import {
  roleDefinitions,
  roleIds,
  type RoleId,
  type AccountRequestList,
  type AccountRequestDetail,
  type AccountRequestAudit,
  type AccountDecisionResult,
} from "@proof/core";
import { useWorkspaceTransport } from "./WorkspaceContext";
import { Button, Control } from "./components";
const messages: Record<string, string> = {
  TENANT_ACCOUNT_REQUEST_STALE:
    "The request or workspace access changed. Review the refreshed request before deciding again.",
  TENANT_ACCOUNT_REQUEST_DECIDED:
    "Another decision is already recorded. Review its details below.",
  TENANT_ACCOUNT_ALREADY_ACTIVE:
    "This subject already has active membership. Use the role editor to change existing access.",
  TENANT_ACCOUNT_REQUEST_SOURCE_UNAVAILABLE:
    "The account-request source is unavailable. Try refreshing the inbox later.",
};
export function AccountRequests() {
  const { api } = useWorkspaceTransport();
  const [inbox, setInbox] = useState<AccountRequestList & { demo?: boolean }>(),
    [detail, setDetail] = useState<AccountRequestDetail>(),
    [audits, setAudits] = useState<AccountRequestAudit[]>([]),
    [roles, setRoles] = useState<RoleId[]>([]),
    [reason, setReason] = useState(""),
    [filter, setFilter] = useState("all"),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState("");
  const reviewGeneration = useRef(0);
  async function refresh() {
    const next = await api<AccountRequestList & { demo?: boolean }>(
      "/account-requests",
    );
    setInbox(next);
    setAudits(await api<AccountRequestAudit[]>("/account-request-audit"));
    return next;
  }
  async function review(id: string) {
    const generation = ++reviewGeneration.current;
    const next = await api<AccountRequestDetail>(
      `/account-requests/${encodeURIComponent(id)}`,
    );
    if (generation !== reviewGeneration.current) return;
    setDetail(next);
    setRoles(next.decision?.roleIds || []);
    setReason(next.decision?.reason || "");
  }
  useEffect(() => {
    void refresh().catch((e) => setMessage(messages[e.message] || e.message));
  }, []);
  async function sample() {
    setBusy(true);
    setMessage("");
    try {
      const created = await api<AccountRequestDetail>(
        "/demo/account-requests",
        { method: "POST", body: "{}" },
      );
      await refresh();
      await review(created.request.id);
    } catch (e) {
      setMessage(messages[(e as Error).message] || (e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function decide(action: "approve" | "reject") {
    if (!detail) return;
    setBusy(true);
    setMessage("");
    const id = detail.request.id;
    try {
      const result = await api<AccountDecisionResult>(
        `/account-requests/${encodeURIComponent(id)}/decision`,
        {
          method: "POST",
          body: JSON.stringify({
            action,
            expectedRequestRevision: detail.request.requestRevision,
            expectedStateRevision: detail.stateRevision,
            ...(action === "approve" ? { roleIds: roles } : { reason }),
          }),
        },
      );
      setDetail(result);
      await refresh();
      setMessage(
        result.replayed
          ? "This decision was already recorded."
          : action === "approve"
            ? "Request approved. Membership roles are active."
            : "Request rejected. No membership was created.",
      );
    } catch (e) {
      setMessage(messages[(e as Error).message] || (e as Error).message);
      try {
        await refresh();
        await review(id);
      } catch {
        /* Preserve the last visible decision on transport failure. */
      }
    } finally {
      setBusy(false);
    }
  }
  const pending = detail?.request.status === "pending";
  return (
    <section className="tenant-access account-requests">
      <span className="eyebrow">WORKSPACE</span>
      <h1>Account requests</h1>
      <InviteWorkspaceUser />
      <p>
        Review requests supplied by your identity system. Approval grants only
        the roles you select.
      </p>
      {message && <p role="status">{message}</p>}
      <div className="setup-actions">
        <Button
          disabled={busy}
          onClick={() =>
            void refresh().catch((e) =>
              setMessage(messages[e.message] || e.message),
            )
          }
        >
          Refresh requests
        </Button>
        {inbox?.demo && (
          <Button disabled={busy} onClick={() => void sample()}>
            Add demo request
          </Button>
        )}
      </div>
      <Control label="Request status">
        <select value={filter} onChange={(e) => setFilter(e.target.value)}>
          {["all", "pending", "approved", "rejected"].map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </Control>
      <div className="account-request-list">
        {inbox?.requests
          .filter((r) => filter === "all" || r.status === filter)
          .map((request) => (
            <article key={request.id} data-account-request={request.id}>
              <div>
                <strong>{request.subjectId}</strong>
                <small>{request.id}</small>
                <span>
                  {request.status} · revision {request.requestRevision}
                </span>
              </div>
              <button
                className="button"
                aria-label={`Review request ${request.id}`}
                disabled={busy}
                onClick={() =>
                  void review(request.id).catch((e) =>
                    setMessage(messages[e.message] || e.message),
                  )
                }
              >
                Review
              </button>
            </article>
          ))}
      </div>
      {inbox && !inbox.requests.length && (
        <p>No account requests have been received.</p>
      )}
      {detail && (
        <section className="access-editor" aria-label="Request decision">
          <h2>Review request</h2>
          <p className="account-request-identity">
            {detail.request.subjectId}
            <br />
            <small>
              {detail.request.id} · {detail.request.status}
            </small>
          </p>
          {pending ? (
            <>
              <fieldset disabled={busy}>
                <legend>Approval roles</legend>
                {roleDefinitions.map((role) => (
                  <label key={role.id}>
                    <input
                      type="checkbox"
                      checked={roles.includes(role.id)}
                      onChange={(e) =>
                        setRoles(
                          roleIds.filter((id) =>
                            id === role.id
                              ? e.target.checked
                              : roles.includes(id),
                          ),
                        )
                      }
                    />
                    {role.name}
                  </label>
                ))}
              </fieldset>
              <Control
                label="Rejection reason"
                note="Use an operational reason; do not include secrets or sensitive personal information."
              >
                <textarea
                  disabled={busy}
                  value={reason}
                  maxLength={500}
                  onChange={(e) => setReason(e.target.value)}
                />
              </Control>
              <div className="setup-actions">
                <Button
                  kind="dark"
                  disabled={busy || !roles.length}
                  onClick={() => void decide("approve")}
                >
                  Approve request
                </Button>
                <Button
                  disabled={busy || !reason.trim()}
                  onClick={() => void decide("reject")}
                >
                  Reject request
                </Button>
              </div>
            </>
          ) : (
            <div>
              <p>Decision: {detail.request.status}</p>
              {detail.decision?.roleIds.length ? (
                <p>Approved roles: {detail.decision.roleIds.join(", ")}</p>
              ) : null}
              {detail.decision?.reason && (
                <p>Reason: {detail.decision.reason}</p>
              )}
              <p>This decision is retained in the audit history.</p>
            </div>
          )}
        </section>
      )}
      <details>
        <summary>Account request audit</summary>
        <ol className="access-audit">
          {[...audits].reverse().map((audit) => (
            <li key={audit.id}>
              <strong>{audit.subjectId}</strong> · {audit.action}
              <br />
              <small>
                {audit.actorId} · request revision {audit.requestRevision} ·{" "}
                {new Date(audit.at).toLocaleString()}
              </small>
              {audit.roleIds.length > 0 && <p>{audit.roleIds.join(", ")}</p>}
              {audit.reason && <p>{audit.reason}</p>}
            </li>
          ))}
        </ol>
      </details>
    </section>
  );
}
